import React, { useCallback, useEffect, useState } from 'react';
import { jornadaControleAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Scale, Loader2, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';

const fmtMin = (min) => {
  const n = Math.abs(Math.trunc(min || 0));
  return `${Math.floor(n / 60)}h${String(n % 60).padStart(2, '0')}`;
};
const fmtSaldo = (min) => `${min < 0 ? '−' : '+'}${fmtMin(min)}`;

const Saldo = ({ minutos, testid }) => {
  const neg = minutos < 0;
  const zero = minutos === 0;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono font-bold text-xs ${zero ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' : neg ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`} data-testid={testid}>
      {!zero && (neg ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />)}{fmtSaldo(minutos)}
    </span>
  );
};

export const BancoHoras = ({ userId, sedeId }) => {
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));
  const [base, setBase] = useState('trabalhados');
  const [dados, setDados] = useState({ itens: [], totais: { trabalhadas: 0, extras: 0, previstas: 0, saldo: 0 }, dias_uteis: 0 });
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jornadaControleAPI.bancoHoras({ mes, base, user_id: userId || undefined, sede_id: sedeId || undefined });
      setDados(res.data);
    } catch (e) {
      setDados({ itens: [], totais: { trabalhadas: 0, extras: 0, previstas: 0, saldo: 0 }, dias_uteis: 0 });
    } finally {
      setLoading(false);
    }
  }, [mes, base, userId, sedeId]);

  useEffect(() => { carregar(); }, [carregar]);

  const t = dados.totais || {};
  const positivos = dados.itens.filter((i) => i.saldo_minutos > 0).length;
  const negativos = dados.itens.filter((i) => i.saldo_minutos < 0).length;

  return (
    <Card data-testid="banco-horas-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Scale className="h-5 w-5 text-indigo-600" /> Banco de horas do mês
            <span className="ml-2 text-xs font-normal text-gray-500">(trabalhadas + extras aprovadas) − previstas</span>
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-44" data-testid="banco-horas-mes" />
            <Select value={base} onValueChange={setBase}>
              <SelectTrigger className="w-56" data-testid="banco-horas-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trabalhados">Previstas = dias trabalhados</SelectItem>
                <SelectItem value="uteis5">Previstas = dias úteis (Seg–Sex)</SelectItem>
                <SelectItem value="uteis6">Previstas = dias úteis (Seg–Sáb)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div>
        ) : dados.itens.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8" data-testid="banco-horas-vazio">Nenhuma jornada registrada neste mês.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="banco-horas-totais">
              {[
                ['Trabalhadas', fmtMin(t.trabalhadas), 'text-purple-700 dark:text-purple-300'],
                ['Extras aprovadas', fmtMin(t.extras), 'text-amber-700 dark:text-amber-300'],
                ['Previstas', fmtMin(t.previstas), 'text-gray-700 dark:text-gray-200'],
              ].map(([l, v, c]) => (
                <div key={l} className="rounded-lg border bg-gray-50 dark:bg-purple-900/10 p-3">
                  <p className="text-[11px] text-gray-500">{l}</p>
                  <p className={`font-mono font-bold text-lg ${c}`}>{v}</p>
                </div>
              ))}
              <div className="rounded-lg border bg-gray-50 dark:bg-purple-900/10 p-3">
                <p className="text-[11px] text-gray-500">Saldo geral</p>
                <div className="mt-1"><Saldo minutos={t.saldo} testid="banco-horas-saldo-geral" /></div>
              </div>
              <div className="rounded-lg border bg-gray-50 dark:bg-purple-900/10 p-3">
                <p className="text-[11px] text-gray-500">Colaboradores</p>
                <p className="text-xs mt-1"><span className="text-emerald-700 font-semibold">{positivos} positivo{positivos === 1 ? '' : 's'}</span> · <span className="text-red-700 font-semibold">{negativos} negativo{negativos === 1 ? '' : 's'}</span></p>
                {base !== 'trabalhados' && <p className="text-[10px] text-gray-400 mt-1">{dados.dias_uteis} dias úteis até hoje</p>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="banco-horas-tabela">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2 font-semibold">Colaborador</th>
                    <th className="text-center p-2 font-semibold">Jornada diária</th>
                    <th className="text-center p-2 font-semibold">Dias</th>
                    <th className="text-center p-2 font-semibold">Trabalhadas</th>
                    <th className="text-center p-2 font-semibold">Extras</th>
                    <th className="text-center p-2 font-semibold">Previstas</th>
                    <th className="text-center p-2 font-semibold">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.itens.map((i) => (
                    <tr key={i.user_id} className="border-b hover:bg-gray-50" data-testid={`banco-horas-row-${i.user_id}`}>
                      <td className="p-2">
                        <span className="font-medium">{i.user_nome}</span>
                        <span className="block text-[10px] text-gray-400">{i.perfil}{i.sede_nome ? ` · ${i.sede_nome}` : ''}</span>
                      </td>
                      <td className="p-2 text-center font-mono">
                        {i.jornada_configurada ? (
                          <>{fmtMin(i.prevista_dia_minutos)}<span className="block text-[10px] text-gray-400">{i.jornada_entrada}–{i.jornada_saida}</span></>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]" title="Cadastre entrada/saída no usuário para calcular as horas previstas"><AlertTriangle className="w-3 h-3 mr-1" />não configurada</Badge>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {i.dias_trabalhados}
                        {base !== 'trabalhados' && <span className="block text-[10px] text-gray-400">de {i.dias_base} úteis</span>}
                        {i.jornadas_abertas > 0 && <span className="block text-[10px] text-emerald-600">{i.jornadas_abertas} em andamento</span>}
                      </td>
                      <td className="p-2 text-center font-mono text-purple-700 dark:text-purple-300">{fmtMin(i.trabalhadas_minutos)}</td>
                      <td className="p-2 text-center font-mono text-amber-700 dark:text-amber-300">{i.extras_minutos ? fmtMin(i.extras_minutos) : '-'}</td>
                      <td className="p-2 text-center font-mono">{i.jornada_configurada ? fmtMin(i.previstas_minutos) : '-'}</td>
                      <td className="p-2 text-center">{i.jornada_configurada ? <Saldo minutos={i.saldo_minutos} testid={`banco-horas-saldo-${i.user_id}`} /> : <span className="text-gray-400 text-xs">-</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BancoHoras;
